import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Shield,
  AlertTriangle,
  Search,
  Filter,
  Eye,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Download,
  ChevronRight,
  X,
  Plus,
  Send,
  RefreshCw,
  BarChart3,
  Archive,
  QrCode,
  Copy,
  Link,
  Printer,
  PieChartIcon,
  FileDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Production domain for public links
const PRODUCTION_DOMAIN = 'https://cerpbrasil.com.br';

// Check if we're in production (deployed to cerpbrasil.com.br)
const isProduction = () => {
  const hostname = window.location.hostname;
  return hostname === 'cerpbrasil.com.br' || hostname === 'www.cerpbrasil.com.br';
};

// Chart colors
const CATEGORY_COLORS = ['#0d9488', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6', '#ec4899', '#10b981', '#6366f1', '#94a3b8'];
const STATUS_COLORS = {
  pending: '#eab308',
  in_progress: '#3b82f6',
  resolved: '#22c55e',
  dismissed: '#64748b'
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock },
  { value: 'in_progress', label: 'Em Análise', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Eye },
  { value: 'resolved', label: 'Resolvido', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle },
  { value: 'dismissed', label: 'Arquivado', color: 'bg-slate-100 text-slate-800 border-slate-300', icon: Archive }
];

const CATEGORIES = [
  "Assédio Moral",
  "Assédio Sexual",
  "Fraude",
  "Corrupção",
  "Discriminação",
  "Desvio de Conduta",
  "Violação de Segurança",
  "Condições de Trabalho",
  "Outros"
];

const WhistleblowerAdmin = () => {
  const { user } = useAuth();
  const qrRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [statistics, setStatistics] = useState({ total: 0, pending: 0, in_progress: 0, resolved: 0, dismissed: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Update form
  const [newStatus, setNewStatus] = useState('');
  const [newNote, setNewNote] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      let url = `${API}/api/whistleblower/admin/reports`;
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
        setStatistics(data.statistics || { total: 0, pending: 0, in_progress: 0, resolved: 0, dismissed: 0 });
      } else if (response.status === 403) {
        toast.error('Acesso negado. Você não tem permissão para acessar este módulo.');
        setReports([]);
        setStatistics({ total: 0, pending: 0, in_progress: 0, resolved: 0, dismissed: 0 });
      } else if (response.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
        setReports([]);
        setStatistics({ total: 0, pending: 0, in_progress: 0, resolved: 0, dismissed: 0 });
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Erro ao carregar denúncias');
        setReports([]);
        setStatistics({ total: 0, pending: 0, in_progress: 0, resolved: 0, dismissed: 0 });
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Erro ao carregar denúncias. Verifique sua conexão.');
      setReports([]);
      setStatistics({ total: 0, pending: 0, in_progress: 0, resolved: 0, dismissed: 0 });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setNewStatus(report.status);
    setAdminResponse(report.admin_response || '');
    setNewNote('');
    setShowDetailModal(true);
  };

  const handleUpdateReport = async () => {
    if (!selectedReport) return;
    
    setUpdating(true);
    try {
      const updateData = {};
      
      if (newStatus !== selectedReport.status) {
        updateData.status = newStatus;
      }
      
      if (adminResponse !== (selectedReport.admin_response || '')) {
        updateData.admin_response = adminResponse;
      }
      
      if (newNote.trim()) {
        updateData.add_note = newNote.trim();
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.info('Nenhuma alteração para salvar');
        return;
      }

      const response = await fetch(`${API}/api/whistleblower/admin/reports/${selectedReport.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Denúncia atualizada com sucesso');
        setSelectedReport(data.report);
        setNewNote('');
        fetchReports();
      } else {
        toast.error('Erro ao atualizar denúncia');
      }
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Erro ao atualizar denúncia');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    const Icon = statusInfo.icon;
    return (
      <Badge className={`${statusInfo.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {statusInfo.label}
      </Badge>
    );
  };

  const filteredReports = reports.filter(report => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        report.tracking_id.toLowerCase().includes(search) ||
        report.category.toLowerCase().includes(search) ||
        report.description.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate the whistleblower submission URL for this company
  // Uses production domain when in production, preview domain otherwise
  const getWhistleblowerUrl = () => {
    const baseUrl = isProduction() ? PRODUCTION_DOMAIN : window.location.origin;
    const companyId = user?.company_id;
    return `${baseUrl}/denuncia/${companyId}`;
  };

  // Copy link to clipboard
  const copyLinkToClipboard = () => {
    const url = getWhistleblowerUrl();
    navigator.clipboard.writeText(url);
    toast.success('Link copiado para a área de transferência!');
  };

  // Prepare chart data
  const getCategoryChartData = () => {
    const categoryCount = {};
    reports.forEach(r => {
      categoryCount[r.category] = (categoryCount[r.category] || 0) + 1;
    });
    return Object.entries(categoryCount).map(([name, value]) => ({ name, value }));
  };

  const getStatusChartData = () => {
    return [
      { name: 'Pendente', value: statistics.pending, fill: STATUS_COLORS.pending },
      { name: 'Em Análise', value: statistics.in_progress, fill: STATUS_COLORS.in_progress },
      { name: 'Resolvido', value: statistics.resolved, fill: STATUS_COLORS.resolved },
      { name: 'Arquivado', value: statistics.dismissed, fill: STATUS_COLORS.dismissed }
    ].filter(item => item.value > 0);
  };

  // Generate PDF Report
  const generatePDFReport = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(13, 148, 136); // teal-600
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Canal de Denúncias', pageWidth / 2, 18, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Relatório Estatístico', pageWidth / 2, 28, { align: 'center' });
      
      doc.setFontSize(10);
      const today = new Date().toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
      });
      doc.text(`Gerado em: ${today}`, pageWidth / 2, 36, { align: 'center' });
      
      // Reset text color
      doc.setTextColor(0, 0, 0);
      
      // Summary Section
      let yPos = 55;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumo Geral', 14, yPos);
      
      yPos += 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      
      // Summary stats
      const summaryData = [
        ['Total de Denúncias', statistics.total.toString()],
        ['Pendentes', statistics.pending.toString()],
        ['Em Análise', statistics.in_progress.toString()],
        ['Resolvidas', statistics.resolved.toString()],
        ['Arquivadas', statistics.dismissed.toString()]
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Indicador', 'Quantidade']],
        body: summaryData,
        theme: 'striped',
        headStyles: { fillColor: [13, 148, 136] },
        margin: { left: 14, right: 14 },
        tableWidth: 80
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      // Category Distribution
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Distribuição por Categoria', 14, yPos);
      
      const categoryData = getCategoryChartData();
      const categoryTableData = categoryData.map(item => [
        item.name, 
        item.value.toString(),
        `${((item.value / statistics.total) * 100).toFixed(1)}%`
      ]);
      
      if (categoryTableData.length > 0) {
        autoTable(doc, {
          startY: yPos + 5,
          head: [['Categoria', 'Quantidade', 'Percentual']],
          body: categoryTableData,
          theme: 'striped',
          headStyles: { fillColor: [13, 148, 136] },
          margin: { left: 14, right: 14 }
        });
        yPos = doc.lastAutoTable.finalY + 15;
      } else {
        yPos += 15;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.text('Nenhuma denúncia registrada.', 14, yPos);
        yPos += 15;
      }
      
      // Date Range
      if (reports.length > 0) {
        const dates = reports.map(r => new Date(r.created_at));
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Período dos Dados', 14, yPos);
        
        yPos += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`De: ${minDate.toLocaleDateString('pt-BR')}`, 14, yPos);
        yPos += 6;
        doc.text(`Até: ${maxDate.toLocaleDateString('pt-BR')}`, 14, yPos);
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          'Relatório gerado automaticamente pelo CERP Brasil - Canal de Denúncias',
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
        doc.text(
          'Este relatório contém apenas dados estatísticos anonimizados.',
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 5,
          { align: 'center' }
        );
      }
      
      // Download
      doc.save(`relatorio-denuncias-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Relatório PDF gerado com sucesso!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erro ao gerar relatório PDF');
    }
  };

  // Download QR Code as PNG
  const downloadQRCode = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) {
      toast.error('Erro ao gerar QR Code');
      return;
    }

    // Create canvas from SVG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    const img = new Image();
    img.onload = () => {
      // Set canvas size with padding for better print quality
      const padding = 40;
      canvas.width = img.width + padding * 2;
      canvas.height = img.height + padding * 2 + 60; // Extra space for text
      
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR Code
      ctx.drawImage(img, padding, padding);
      
      // Add company name text below QR
      ctx.fillStyle = '#0d9488'; // teal-600
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Canal de Denúncias', canvas.width / 2, img.height + padding + 30);
      
      ctx.fillStyle = '#64748b'; // slate-500
      ctx.font = '12px Arial';
      ctx.fillText('Escaneie para fazer uma denúncia anônima', canvas.width / 2, img.height + padding + 50);
      
      // Download
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qrcode-canal-denuncias.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      
      toast.success('QR Code baixado com sucesso!');
    };
    img.src = url;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whistleblower-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Shield className="h-7 w-7 text-teal-600" />
            Canal de Denúncias
          </h1>
          <p className="text-slate-500 mt-1">Gerencie as denúncias anônimas recebidas</p>
        </div>
        <Button onClick={fetchReports} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-700">{statistics.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">{statistics.pending}</p>
              <p className="text-sm text-yellow-600">Pendentes</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{statistics.in_progress}</p>
              <p className="text-sm text-blue-600">Em Análise</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{statistics.resolved}</p>
              <p className="text-sm text-green-600">Resolvidos</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-600">{statistics.dismissed}</p>
              <p className="text-sm text-slate-500">Arquivados</p>
            </CardContent>
          </Card>
        </div>

      {/* QR Code & Link Section */}
      <Card className="bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-200">
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* QR Code Preview */}
            <div 
              ref={qrRef}
              className="bg-white p-4 rounded-xl shadow-sm border border-teal-100 flex-shrink-0"
            >
              <QRCodeSVG 
                value={getWhistleblowerUrl()} 
                size={120}
                level="H"
                includeMargin={true}
                fgColor="#0d9488"
              />
            </div>
            
            {/* Link & Actions */}
            <div className="flex-1 space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-teal-800 flex items-center gap-2">
                  <QrCode className="h-5 w-5" />
                  Link do Canal de Denúncias
                </h3>
                <p className="text-sm text-teal-600 mt-1">
                  Compartilhe este link ou QR Code com seus funcionários para denúncias anônimas
                </p>
              </div>
              
              {/* URL Display */}
              <div className="flex items-center gap-2 bg-white rounded-lg border border-teal-200 p-2">
                <Link className="h-4 w-4 text-teal-500 flex-shrink-0 ml-1" />
                <code className="text-sm text-slate-700 flex-1 truncate">
                  {getWhistleblowerUrl()}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyLinkToClipboard}
                  className="text-teal-600 hover:text-teal-700 hover:bg-teal-100 flex-shrink-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setShowQRModal(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white gap-2"
                >
                  <QrCode className="h-4 w-4" />
                  Ver QR Code Ampliado
                </Button>
                <Button
                  onClick={downloadQRCode}
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50 gap-2"
                >
                  <Download className="h-4 w-4" />
                  Baixar QR Code (PNG)
                </Button>
                <Button
                  onClick={copyLinkToClipboard}
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50 gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Copiar Link
                </Button>
                <Button
                  onClick={generatePDFReport}
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50 gap-2"
                >
                  <FileDown className="h-4 w-4" />
                  Gerar Relatório PDF
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Section */}
      {statistics.total > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Category Distribution - Pie Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <PieChartIcon className="h-5 w-5 text-teal-600" />
                Distribuição por Categoria
              </CardTitle>
              <CardDescription>
                Proporção de denúncias por tipo
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={getCategoryChartData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getCategoryChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Status Distribution - Bar Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-teal-600" />
                Status das Denúncias
              </CardTitle>
              <CardDescription>
                Quantidade por status de resolução
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={getStatusChartData()}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value) => [value, 'Quantidade']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {getStatusChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por protocolo, categoria ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">Todos os status</option>
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">Todas as categorias</option>
              <option value="Assédio Moral">Assédio Moral</option>
              <option value="Assédio Sexual">Assédio Sexual</option>
              <option value="Fraude">Fraude</option>
              <option value="Corrupção">Corrupção</option>
              <option value="Discriminação">Discriminação</option>
              <option value="Desvio de Conduta">Desvio de Conduta</option>
              <option value="Violação de Segurança">Violação de Segurança</option>
              <option value="Condições de Trabalho">Condições de Trabalho</option>
              <option value="Outros">Outros</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Denúncias Recebidas
            <Badge variant="secondary" className="ml-2">{filteredReports.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredReports.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Shield className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Nenhuma denúncia encontrada</p>
              {(statusFilter || categoryFilter || searchTerm) && (
                <Button
                  variant="link"
                  onClick={() => {
                    setStatusFilter('');
                    setCategoryFilter('');
                    setSearchTerm('');
                  }}
                  className="mt-2"
                >
                  Limpar filtros
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => handleViewReport(report)}
                  data-testid={`report-item-${report.tracking_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-teal-600">{report.tracking_id}</span>
                        {getStatusBadge(report.status)}
                        <Badge variant="outline">{report.category}</Badge>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{report.description}</p>
                      <p className="text-xs text-slate-400">
                        Registrado em {formatDate(report.created_at)}
                        {report.admin_notes?.length > 0 && (
                          <span className="ml-3">
                            <MessageSquare className="h-3 w-3 inline mr-1" />
                            {report.admin_notes.length} nota(s)
                          </span>
                        )}
                        {report.attachments?.length > 0 && (
                          <span className="ml-3">
                            <FileText className="h-3 w-3 inline mr-1" />
                            {report.attachments.length} anexo(s)
                          </span>
                        )}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-slate-100 px-6 py-4 flex items-center justify-between border-b">
              <div>
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-teal-600" />
                  Denúncia {selectedReport.tracking_id}
                </h2>
                <p className="text-sm text-slate-500">{selectedReport.category}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetailModal(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
              {/* Status & Dates */}
              <div className="flex items-center gap-4 text-sm">
                {getStatusBadge(selectedReport.status)}
                <span className="text-slate-500">
                  Registrado: {formatDate(selectedReport.created_at)}
                </span>
                {selectedReport.updated_at !== selectedReport.created_at && (
                  <span className="text-slate-500">
                    Atualizado: {formatDate(selectedReport.updated_at)}
                  </span>
                )}
              </div>

              {/* Description */}
              <div>
                <Label className="text-slate-700 font-semibold mb-2 block">Descrição da Denúncia</Label>
                <div className="bg-slate-50 rounded-lg p-4 text-slate-700 whitespace-pre-wrap">
                  {selectedReport.description}
                </div>
              </div>

              {/* Attachments */}
              {selectedReport.attachments?.length > 0 && (
                <div>
                  <Label className="text-slate-700 font-semibold mb-2 block">Anexos</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedReport.attachments.map((url, idx) => (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors"
                      >
                        <FileText className="h-4 w-4" />
                        Anexo {idx + 1}
                        <Download className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes (Internal) */}
              <div>
                <Label className="text-slate-700 font-semibold mb-2 block flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Notas Internas (não visíveis ao denunciante)
                </Label>
                <div className="space-y-2 mb-3">
                  {selectedReport.admin_notes?.length > 0 ? (
                    selectedReport.admin_notes.map((note, idx) => (
                      <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <p className="text-slate-700">{note.note}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {note.author} • {formatDate(note.timestamp)}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 italic">Nenhuma nota interna registrada</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Adicionar nota interna..."
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (newNote.trim()) {
                        handleUpdateReport();
                      }
                    }}
                    disabled={!newNote.trim() || updating}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Admin Response (Visible to Reporter) */}
              <div>
                <Label className="text-slate-700 font-semibold mb-2 block flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Resposta ao Denunciante (visível ao consultar protocolo)
                </Label>
                <Textarea
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Escreva uma resposta que será visível ao denunciante quando ele consultar o protocolo..."
                  className="min-h-[100px]"
                />
              </div>

              {/* Status Update */}
              <div>
                <Label className="text-slate-700 font-semibold mb-2 block">Alterar Status</Label>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((status) => {
                    const Icon = status.icon;
                    return (
                      <button
                        key={status.value}
                        onClick={() => setNewStatus(status.value)}
                        className={`px-4 py-2 rounded-lg border flex items-center gap-2 transition-all ${
                          newStatus === status.value
                            ? status.color + ' ring-2 ring-offset-2 ring-slate-300'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {status.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t">
              <Button
                variant="outline"
                onClick={() => setShowDetailModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateReport}
                disabled={updating}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {updating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="bg-teal-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <QrCode className="h-5 w-5" />
                QR Code - Canal de Denúncias
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQRModal(false)}
                className="text-white hover:bg-teal-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6 text-center space-y-4">
              {/* Large QR Code */}
              <div className="bg-white p-6 rounded-xl border-2 border-teal-100 inline-block">
                <QRCodeSVG 
                  value={getWhistleblowerUrl()} 
                  size={250}
                  level="H"
                  includeMargin={true}
                  fgColor="#0d9488"
                />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-teal-800">Canal de Denúncias Anônimas</h3>
                <p className="text-sm text-slate-500">
                  Escaneie o QR Code para fazer uma denúncia anônima e confidencial
                </p>
              </div>

              {/* URL */}
              <div className="bg-slate-50 rounded-lg p-3">
                <code className="text-xs text-slate-600 break-all">
                  {getWhistleblowerUrl()}
                </code>
              </div>

              {/* Actions */}
              <div className="flex justify-center gap-3 pt-2">
                <Button
                  onClick={downloadQRCode}
                  className="bg-teal-600 hover:bg-teal-700 text-white gap-2"
                >
                  <Download className="h-4 w-4" />
                  Baixar PNG
                </Button>
                <Button
                  onClick={() => {
                    window.print();
                  }}
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50 gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Imprimir
                </Button>
              </div>
              
              <p className="text-xs text-slate-400 pt-2">
                Dica: Imprima e afixe em murais, refeitórios e áreas comuns da empresa
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhistleblowerAdmin;
